import {useState, useEffect} from 'preact/hooks'
import {
    FilterOptions,
    SearchFilters,
    SortSectionPayload,
    TooltipTextsProps,
    FilterItem,
    PrefillFiltersProps,
    KuratierteReiseItem,
    ExtIdItem, UpdateFilterItem, GrundsortierungProps, FilterOption
} from '../types/travel'

import {DurationPicker} from './DurationPicker'
import {DestinationSearchV2} from './DestinationSearchV2'
import {FilterSection} from "./FilterSection.tsx";
import {TravelDateSelector} from "./TravelDateSelector.tsx";
import {IconChevron} from "./svg-icons/IconChevron.tsx";
import {IconClose} from "./svg-icons/IconClose.tsx";
import {PriceSlider} from "./PriceSlider.tsx";
import {getSectionVisibilityClass} from "../utils/htmlUtils.ts";
import {SortSection} from "./SortSection.tsx";
import {ActiveFilters} from "./ActiveFilters.tsx";


interface FilterMobileProps {
    filters: SearchFilters
    onFiltersChange: (filters: SearchFilters) => void
    onSortChange?: (payload: SortSectionPayload) => void
    isFilterOverlayOpen: boolean
    setIsFilterOverlayOpen: (isOpen: boolean) => void
    filterOptions: FilterOptions
    toolTipTexts?: TooltipTextsProps
    prefillFilters?: PrefillFiltersProps
    total?: number
    currentGridToggleViewState: boolean
    gridViewState: boolean
    setGridViewState: (value: boolean) => void
    kuratierteReisen?: KuratierteReiseItem[]
    extIdItems?: ExtIdItem[]
    grundsortierung?: GrundsortierungProps
    hideEmptyFilters?: boolean
}

function sortByOrder(options: FilterOption[], order: string[] | undefined): FilterOption[] {
    if (!order || order.length === 0) return options;
    return [...options].sort((a, b) => {
        const ai = order.indexOf(a.value);
        const bi = order.indexOf(b.value);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
    });
}


export function FilterMobile({
                                 filters,
                                 onFiltersChange,
                                 onSortChange,
                                 isFilterOverlayOpen,
                                 setIsFilterOverlayOpen,
                                 filterOptions,
                                 toolTipTexts,
                                 total,
                                 gridViewState,
                                 setGridViewState,
                                 kuratierteReisen,
                                 extIdItems,
                                 prefillFilters,
                                 currentGridToggleViewState,
                                 grundsortierung,
                                 hideEmptyFilters = false,
                            }: FilterMobileProps) {

    const [isOpenShowMoreFilters, setIsOpenShowMoreFilters] = useState(true);
    const [fadeIn, setFadeIn] = useState(false);
    const [sortState, setSortState] = useState<string>('customsort');

    const handleShowMoreFilters = (current: boolean) => {
        setIsOpenShowMoreFilters(!current)
    }

    const handleCloseFilterOverlay = () => {
        setIsFilterOverlayOpen(false)
    }

    const updateFilter = (category: keyof SearchFilters, value: string, checked: boolean, override: boolean = false) => {
        const current = (filters[category] as string[]) || []
        let updated = checked
            ? [...current, value]
            : current.filter(v => v !== value)

        if(override) {

        }

        onFiltersChange({
            ...filters,
            [category]: updated
        })
    }

    const updateFilterArray = (category: keyof SearchFilters, values: string[]) => {
        onFiltersChange({
            ...filters,
            [category]: values
        })
    }

    const updateMultiFilterArray = (updateFilterItems: UpdateFilterItem[]) => {
        const updatedFilters = { ...filters };

        updateFilterItems.forEach(({ category, values }) => {
            if (category === 'start' || category === 'end') {
                // These fields expect string, not string[]
                updatedFilters[category] = Array.isArray(values) ? values[0] : values;
            } else {
                // Other fields expect string[]
                updatedFilters[category] = Array.isArray(values) ? values : [values];
            }
        });

        onFiltersChange(updatedFilters);
    }


    useEffect(() => {
        if (isFilterOverlayOpen) {
            document.body.style.setProperty('overflow', 'hidden');
            document.body.style.setProperty('height', '100vh');
        } else {
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('height');
        }
        return () => {
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('height');
        }
    }, [isFilterOverlayOpen])


    //close overlay on resize
    useEffect(() => {
        const closeFilterOverlay = () => {
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('height');
            setIsFilterOverlayOpen(false)
        }

        window.addEventListener('resize', closeFilterOverlay);
        return () => window.removeEventListener('resize', closeFilterOverlay);
    }, [isFilterOverlayOpen]);

    // trigger fade-in after the overlay is added to DOM
    useEffect(() => {
        if (isFilterOverlayOpen) {
            const timeout = setTimeout(() => setFadeIn(true), 10); // small delay to trigger CSS transition
            return () => clearTimeout(timeout);
        } else {
            setFadeIn(false);
        }
    }, [isFilterOverlayOpen]);


    function enrichWithTooltips(
        tooltipTexts: TooltipTextsProps,
        filterOptions: FilterOptions
    ): FilterOptions {
        // nur shallow copy, Arrays werden noch referenziert
        const result: FilterOptions = { ...filterOptions };

        for (const [category, tooltips] of Object.entries(tooltipTexts)) {
            // @ts-ignore
            const items = result[category];
            if (Array.isArray(items)) {
                items.forEach((item: FilterItem) => {
                    const tooltip = tooltips[item.value as string];
                    if (tooltip) {
                        item.tooltip = tooltip;
                    }
                });
            }
        }

        return result;
    }

    const enrichedFilterOptions = toolTipTexts
        ? enrichWithTooltips(toolTipTexts, filterOptions)
        : filterOptions;

    return (
        <>
            {/* Suchfelder */}
            <div className={getSectionVisibilityClass('destination', prefillFilters)}>
                <DestinationSearchV2
                    options={filterOptions.destination}
                    cssClasses={"mb-5"}
                    selectedValues={filters.destination || []}
                    kuratierteReisen={kuratierteReisen || []}
                    onSelectionChange={(values) => {
                        onFiltersChange({
                            ...filters,
                            destination: values
                        })
                    }}
                    extIdItems={extIdItems}
                />
            </div>

            <TravelDateSelector
                gridViewState={gridViewState}
                setGridViewState={setGridViewState}
                options={filterOptions.yearmonth || []}
                selectedValues={filters.yearmonth || []}
                onSelectionChange={updateFilterArray}
                onDateSelection={updateMultiFilterArray}
                initialStart={filters.start}
                initialEnd={filters.end}
                cssClasses={"mb-9"}/>

            {isFilterOverlayOpen && (
                <div className={`
                    fixed top-0 bottom-0 left-0 right-0 bg-white w-full h-screen z-100 transition-opacity ease-linear 
                    ${fadeIn ? "opacity-100" : "opacity-0"}
                `}>

                    <div className="sticky top-0 py-5 border-b-2 border-solid border-theme-gray-light text-center">
                        <span className="font-reckless font-semibold text-20px  text-theme-primary">Filtern</span>
                        <button
                            className="filter-overlay-close absolute top-1/2 -translate-y-1/2 right-[30px]"
                            onClick={() => handleCloseFilterOverlay()}>
                            <IconClose/>
                        </button>
                    </div>

                    <div className="www-inner h-[calc(100vh_-_174px)]  overflow-y-auto">
                        <div className="pt-[30px] pb-[130px] px-8">
                            {/* Suchfelder */}
                            <div className={getSectionVisibilityClass('destination', prefillFilters)}>
                                <DestinationSearchV2
                                    options={filterOptions.destination}
                                    cssClasses={"mb-5"}
                                    selectedValues={filters.destination || []}
                                    kuratierteReisen={kuratierteReisen || []}
                                    onSelectionChange={(values) => {
                                        onFiltersChange({
                                            ...filters,
                                            destination: values
                                        })
                                    }}
                                    extIdItems={extIdItems}
                                />
                            </div>

                            <TravelDateSelector
                                gridViewState={gridViewState}
                                setGridViewState={setGridViewState}
                                options={filterOptions.yearmonth || []}
                                selectedValues={filters.yearmonth || []}
                                onSelectionChange={updateFilterArray}
                                onDateSelection={updateMultiFilterArray}
                                initialStart={filters.start}
                                initialEnd={filters.end}
                                cssClasses={"mb-9"} />

                            {currentGridToggleViewState && (
                                <SortSection
                                    title={"Sortierung"}
                                    section="sortierung-mobil"
                                    options={[
                                        {value: 'customsort', label: 'Empfehlung'},
                                        {value: 'preis', label: 'Niedrigster Preis'},
                                        {value: 'anreise', label: 'Frühester Reisebeginn'},
                                        {value: 'wertschoepfung', label: 'Höchste lokale Wertschöpfung'},
                                    ]}
                                    onSortChange={(payload: SortSectionPayload) => {
                                        if(onSortChange) {
                                            onSortChange(payload)
                                        }
                                    }}
                                    sectionClosable={true}
                                    defaultValue={sortState}
                                    setDefaultValue={(value: string) => {
                                        setSortState(value)
                                    }}
                                />
                            )}

                            <ActiveFilters
                                filters={filters}
                                onFiltersChange={onFiltersChange}
                                cssClasses={`block lg:hidden`}
                                prefillFilters={prefillFilters}
                            />


                            {(!hideEmptyFilters || (filters.reiseart && filters.reiseart.length > 0)) && (
                                <div className={getSectionVisibilityClass('reiseart', prefillFilters)}>
                                    <FilterSection
                                        title="Reiseart"
                                        titleToolTip={toolTipTexts?.reiseart?.parent}
                                        section="reiseart"
                                        options={sortByOrder(enrichedFilterOptions.reiseart || [], grundsortierung?.reiseart)}
                                        filters={filters}
                                        onFilterUpdate={updateFilter}
                                    />
                                </div>
                            )}

                            {(!hideEmptyFilters || (filters.difficulty && filters.difficulty.length > 0)) && (
                                <div className={getSectionVisibilityClass('difficulty', prefillFilters)}>
                                    <FilterSection
                                        title="Schwierigkeitsgrade"
                                        titleToolTip={toolTipTexts?.difficulty?.parent}
                                        section="difficulty"
                                        options={enrichedFilterOptions.difficulty || []}
                                        filters={filters}
                                        onFilterUpdate={updateFilter}
                                    />
                                </div>
                            )}

                            {(!hideEmptyFilters || (filters.price && filters.price.length > 0)) && (
                                <PriceSlider
                                    minPrice={filterOptions.priceRange?.min || 0}
                                    maxPrice={filterOptions.priceRange?.max || 5000}
                                    activeFilter={filters.price}
                                    onPriceChange={(min, max) => {
                                        onFiltersChange({
                                            ...filters,
                                            price: [`${min}-${max}`]
                                        })
                                    }}
                                />
                            )}

                            {(!hideEmptyFilters || (filters.duration && filters.duration.length > 0)) && (
                                <DurationPicker
                                    options={filterOptions.duration || []}
                                    selectedValues={filters.duration}
                                    onDurationChange={updateFilter}
                                />
                            )}

                            {(!hideEmptyFilters || ['anreiseart', 'unterkunft', 'veranstalter', 'standortwechsel', 'empfehlung', 'interesse'].some(k => ((filters[k as keyof SearchFilters] as string[]) || []).length > 0)) && (
                                <>
                                    <h2>
                                        <button className="w-full flex justify-between items-center" onClick={() => handleShowMoreFilters(isOpenShowMoreFilters)}>
                                            Weitere
                                            Filter <span><IconChevron cssClasses={isOpenShowMoreFilters ? 'rotate-0' : 'rotate-180'}/></span>
                                        </button>
                                    </h2>

                                    {isOpenShowMoreFilters && (
                                        <>
                                            {(!hideEmptyFilters || (filters.anreiseart && filters.anreiseart.length > 0)) && (
                                                <div className={getSectionVisibilityClass('anreiseart', prefillFilters)}>
                                                    <FilterSection
                                                        title="Anreise"
                                                        titleToolTip={toolTipTexts?.anreiseart?.parent}
                                                        section="anreiseart"
                                                        options={enrichedFilterOptions.anreiseart || []}
                                                        filters={filters}
                                                        onFilterUpdate={updateFilter}
                                                    />
                                                </div>
                                            )}

                                            {(!hideEmptyFilters || (filters.unterkunft && filters.unterkunft.length > 0)) && (
                                                <div className={getSectionVisibilityClass('unterkunft', prefillFilters)}>
                                                    <FilterSection
                                                        title="Unterkunftstyp"
                                                        titleToolTip={toolTipTexts?.unterkunft?.parent}
                                                        section="unterkunft"
                                                        options={sortByOrder(enrichedFilterOptions.unterkunft || [], grundsortierung?.unterkunft)}
                                                        filters={filters}
                                                        onFilterUpdate={updateFilter}
                                                    />
                                                </div>
                                            )}

                                            {(!hideEmptyFilters || (filters.veranstalter && filters.veranstalter.length > 0)) && (
                                                <FilterSection
                                                    title="Veranstalter"
                                                    section="veranstalter"
                                                    titleToolTip={toolTipTexts?.veranstalter?.parent}
                                                    options={sortByOrder(enrichedFilterOptions.veranstalter || [], grundsortierung?.veranstalter)}
                                                    filters={filters}
                                                    onFilterUpdate={updateFilter}
                                                />
                                            )}

                                            {(!hideEmptyFilters || (filters.standortwechsel && filters.standortwechsel.length > 0)) && (
                                                <FilterSection
                                                    title="Standortwechsel"
                                                    section="standortwechsel"
                                                    titleToolTip={toolTipTexts?.standortwechsel?.parent}
                                                    options={enrichedFilterOptions.standortwechsel || []}
                                                    filters={filters}
                                                    onFilterUpdate={updateFilter}
                                                />
                                            )}

                                            {(!hideEmptyFilters || (filters.empfehlung && filters.empfehlung.length > 0)) && (
                                                <FilterSection
                                                    title="Empfehlungen"
                                                    section="empfehlung"
                                                    titleToolTip={toolTipTexts?.empfehlung?.parent}
                                                    options={enrichedFilterOptions.empfehlung || []}
                                                    filters={filters}
                                                    onFilterUpdate={updateFilter}
                                                />
                                            )}

                                            {(!hideEmptyFilters || (filters.interesse && filters.interesse.length > 0)) && (
                                                <div className={getSectionVisibilityClass('interesse', prefillFilters)}>
                                                    <FilterSection
                                                        title="Interessen"
                                                        section="interesse"
                                                        titleToolTip={toolTipTexts?.interesse?.parent}
                                                        options={enrichedFilterOptions.interesse || []}
                                                        filters={filters}
                                                        onFilterUpdate={updateFilter}
                                                        cssClasses={"border-0!"}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    <div className="fixed w-full z-100 bottom-0 px-12 py-7 bg-white text-center shadow-filter-overlay-bottom">
                        <button onClick={handleCloseFilterOverlay} className="btn height-50 red w-full">{total} {gridViewState ? 'Reisen' : 'Termine'} finden</button>
                    </div>
                </div>
            )}
        </>
    )
}
